import { VARNA } from './jyotish_varna';

/**
 * Deterministic, dictionary-driven texts for the Varna Profile.
 * Tendencies only: no hierarchy, no status, no career predictions, no Varna names.
 *
 * Phrase fields are composed into sentences, so they keep a fixed grammatical form:
 * - verb: EN base form / RU 2nd person singular («ты …»)
 * - role: EN/RU clause "quality + what it gives", lower-case
 * - pull: EN noun phrase / RU «к» + dative
 * - expression: EN noun phrase / RU accusative after «через»
 * - resource: EN noun / RU nominative
 * - outcome: EN noun phrase / RU instrumental
 * - need: EN noun phrase / RU instrumental (after «между»)
 */
export const VARNA_INTERPRETATION = {
  brahmin: {
    en: {
      motivation: 'Seeks understanding, knowledge, patterns and meaning.',
      approach: 'Observes first, looks for principles and builds a coherent picture before committing.',
      strengths: 'Depth, clarity of thought, ability to explain and to see the whole.',
      challenges: 'May stay in analysis too long or postpone action until everything is fully clear.',
      contribution: 'Brings perspective, meaning and well-grounded ideas to others.',
      verb: 'understand',
      role: 'understanding gives meaning',
      pull: 'knowledge and meaning',
      expression: 'reflection, explanation and clarity',
      resource: 'insight',
      outcome: 'something meaningful and clearly understood',
      need: 'the wish to understand everything first',
    },
    ru: {
      motivation: 'Ищет понимание, знания, закономерности и смысл.',
      approach: 'Сначала наблюдает, ищет принципы и складывает целостную картину, прежде чем действовать.',
      strengths: 'Глубина, ясность мышления, умение объяснять и видеть целое.',
      challenges: 'Может слишком долго оставаться в анализе или откладывать действие, пока всё не станет полностью ясно.',
      contribution: 'Даёт другим перспективу, смысл и обоснованные идеи.',
      verb: 'понимаешь',
      role: 'понимание даёт смысл',
      pull: 'к пониманию и смыслу',
      expression: 'размышление, объяснение и ясность',
      resource: 'глубокое понимание',
      outcome: 'чем-то осмысленным и понятным другим',
      need: 'желанием сначала всё до конца понять',
    },
  },
  kshatriya: {
    en: {
      motivation: 'Acts, protects, leads and takes responsibility.',
      approach: 'Moves toward a goal, makes decisions and sets direction when a situation needs it.',
      strengths: 'Initiative, courage, decisiveness and readiness to stand up for what matters.',
      challenges: 'May push too hard, act before listening, or carry too much alone.',
      contribution: 'Gives others direction, protection and momentum.',
      verb: 'direct',
      role: 'action sets direction',
      pull: 'action and leadership',
      expression: 'initiative, decisiveness and responsibility',
      resource: 'your drive',
      outcome: 'clear direction and real movement',
      need: 'the urge to act right away',
    },
    ru: {
      motivation: 'Действует, защищает, ведёт и берёт ответственность.',
      approach: 'Движется к цели, принимает решения и задаёт направление, когда ситуация этого требует.',
      strengths: 'Инициатива, смелость, решительность и готовность отстаивать важное.',
      challenges: 'Может слишком давить, действовать раньше, чем выслушать, или брать всё на себя.',
      contribution: 'Даёт другим направление, защиту и импульс.',
      verb: 'направляешь',
      role: 'действие задаёт направление',
      pull: 'к действию и лидерству',
      expression: 'инициативу, решительность и ответственность',
      resource: 'решимость',
      outcome: 'ясным направлением и реальным движением',
      need: 'стремлением действовать сразу',
    },
  },
  vaishya: {
    en: {
      motivation: 'Connects people, resources and opportunities.',
      approach: 'Notices where movement and value arise, builds relationships and organizes exchange.',
      strengths: 'Sociability, flexibility, a sense of value and of good timing.',
      challenges: 'May spread attention too widely or keep too many options open.',
      contribution: 'Creates connections, circulation and shared growth.',
      verb: 'connect',
      role: 'connection creates movement',
      pull: 'growth and exchange',
      expression: 'connections, exchange and flexibility',
      resource: 'your ability to connect',
      outcome: 'living exchange and mutual growth',
      need: 'the wish to keep options open',
    },
    ru: {
      motivation: 'Соединяет людей, ресурсы и возможности.',
      approach: 'Замечает, где возникает движение и ценность, выстраивает связи и организует обмен.',
      strengths: 'Общительность, гибкость, чувство ценности и подходящего момента.',
      challenges: 'Может распылять внимание или держать открытыми слишком много вариантов.',
      contribution: 'Создаёт связи, движение ресурсов и общий рост.',
      verb: 'соединяешь',
      role: 'связи создают движение',
      pull: 'к росту и взаимодействию',
      expression: 'связи, обмен и гибкость',
      resource: 'умение соединять',
      outcome: 'живым обменом и взаимным ростом',
      need: 'желанием сохранять гибкость и открытые возможности',
    },
  },
  shudra: {
    en: {
      motivation: 'Builds, practices, improves and creates tangible results.',
      approach: 'Works with form and detail, learns by doing and brings tasks to completion.',
      strengths: 'Reliability, skill, patience and attention to quality.',
      challenges: 'May undervalue own contribution or get absorbed in details.',
      contribution: 'Turns plans into something that works and can be relied on.',
      verb: 'embody',
      role: 'craft gives form',
      pull: 'craft and tangible results',
      expression: 'craftsmanship, precision and practical results',
      resource: 'your craft',
      outcome: 'something useful, functional or carefully crafted',
      need: 'the need for a concrete, tangible result',
    },
    ru: {
      motivation: 'Строит, практикует, совершенствует и создаёт осязаемый результат.',
      approach: 'Работает с формой и деталями, учится в деле и доводит задачи до конца.',
      strengths: 'Надёжность, мастерство, терпение и внимание к качеству.',
      challenges: 'Может недооценивать свой вклад или уходить в детали.',
      contribution: 'Превращает замыслы в то, что работает и на что можно опереться.',
      verb: 'воплощаешь',
      role: 'мастерство даёт форму',
      pull: 'к делу и мастерству',
      expression: 'мастерство, точность и практический результат',
      resource: 'мастерство',
      outcome: 'чем-то полезным, работающим и тщательно сделанным',
      need: 'потребностью получить конкретный, осязаемый результат',
    },
  },
};

const capitalize = (text) => (text ? text[0].toUpperCase() + text.slice(1) : text);

/** Wrap a varna cue (verb / pull / expression) for UI bolding. */
const bold = (text) => (text ? `**${text}**` : text);

const joinList = (items, and) =>
  items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;

const joinClauses = (items, conj) =>
  items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')}, ${conj} ${items[items.length - 1]}`;

const TEMPLATES = {
  en: {
    clauses: (parts) => joinClauses(parts, 'and'),
    titles: {
      core: 'Core nature',
      inner: 'Inner potential',
      manifestation: 'Manifestation',
      interaction: 'How it works together',
    },
    motto: (verbs) => (verbs.length === 1 ? `You ${verbs[0]} — on every level.` : `You ${joinList(verbs, 'and')}.`),
    opening: { core: (c) => `At your core you seek ${c.pull}`, inner: (i) => `your inner development leads you toward ${i.pull}`, innerSame: () => 'your inner development deepens the same quality', manifestation: (list) => `in practical life this shows up through ${list.join(', as well as through ')}` },
    core: (c) => `${c.motivation} ${c.strengths}`,
    inner: (i) => `Your inner development is driven by ${i.pull}. ${i.approach}`,
    innerSame: (i) => `Your inner potential repeats your core: ${i.pull} is both what motivates you and the way you grow. This quality is deeply rooted and stable.`,
    manifestationStrong: (m) => `Both the Ascendant and the Sun point here, so this is a strongly expressed pattern: your qualities show up through ${m.expression}. ${m.contribution}`,
    manifestationAscendant: (m) => `In outward expression (Ascendant), your qualities show up through ${m.expression}. ${m.contribution}`,
    manifestationSun: (m) => `Your personal will (Sun) is drawn to ${m.pull}. ${m.strengths}`,
    roles: (roles) => (roles.length === 1 ? `${capitalize(roles[0])} — on every level, which makes this quality especially consistent.` : `${capitalize(joinClauses(roles, 'and'))}.`),
    fulfilment: (c, o) => `You may feel most fulfilled when ${c.resource} becomes ${o.outcome}.`,
    tension: (c, o) => `Inner tension may arise between ${c.need} and ${o.need}.`,
  },
  ru: {
    clauses: (parts) => joinClauses(parts, 'а'),
    titles: {
      core: 'Основная природа',
      inner: 'Внутренний потенциал',
      manifestation: 'Способ реализации',
      interaction: 'Как это сочетается',
    },
    motto: (verbs) => (verbs.length === 1 ? `Ты ${verbs[0]} — на всех уровнях.` : `Ты ${joinList(verbs, 'и')}.`),
    opening: { core: (c) => `В основе ты стремишься ${c.pull}`, inner: (i) => `внутреннее развитие ведёт тебя ${i.pull}`, innerSame: () => 'внутреннее развитие углубляет то же качество', manifestation: (list) => `в практической жизни это проявляется через ${list.join(', а также через ')}` },
    core: (c) => `${c.motivation} ${c.strengths}`,
    inner: (i) => `Внутреннее развитие ведёт тебя ${i.pull}. ${i.approach}`,
    innerSame: (i) => `Внутренний потенциал повторяет основную природу: стремление ${i.pull} — и мотивация, и путь развития. Это качество глубоко укоренено и устойчиво.`,
    manifestationStrong: (m) => `На это указывают и асцендент, и Солнце — это ярко выраженный способ реализации: твои качества проявляются через ${m.expression}. ${m.contribution}`,
    manifestationAscendant: (m) => `Во внешнем проявлении (асцендент) твои качества выражаются через ${m.expression}. ${m.contribution}`,
    manifestationSun: (m) => `Личная воля (Солнце) тянется ${m.pull}. ${m.strengths}`,
    roles: (roles) => (roles.length === 1 ? `${capitalize(roles[0])} — на всех уровнях, поэтому это качество особенно цельное.` : `${capitalize(joinClauses(roles, 'а'))}.`),
    fulfilment: (c, o) => `Наибольшее удовлетворение может приносить деятельность, в которой ${c.resource} становится ${o.outcome}.`,
    tension: (c, o) => `Внутреннее напряжение может возникать между ${c.need} и ${o.need}.`,
  },
};

const resolveLocale = (locale) => (TEMPLATES[locale] ? locale : 'en');

const describe = (varnaId, locale) => {
  const text = VARNA_INTERPRETATION[varnaId]?.[locale];
  if (!text) return null;
  return {
    id: varnaId,
    theme: VARNA[varnaId].theme[locale],
    motivation: text.motivation,
    approach: text.approach,
    strengths: text.strengths,
    challenges: text.challenges,
    contribution: text.contribution,
  };
};

const unique = (items) => [...new Set(items.filter(Boolean))];

/** Levels from the profile; older profiles without `levels` are rebuilt from indicators. */
const resolveLevels = (profile) => {
  if (profile.levels) return profile.levels;
  const find = (source, chartId) => profile.indicators?.find((one) => one.source === source && one.chart === chartId)?.varna || null;
  const asc = find('ascendant', 'D1');
  const sun = find('sun', 'D1');
  const manifestation = asc && asc === sun
    ? [{ varna: asc, sources: ['ascendant', 'sun'], strong: true }]
    : [['ascendant', asc], ['sun', sun]].filter(([, v]) => v).map(([source, varna]) => ({ varna, sources: [source], strong: false }));
  return { core: profile.primary, inner: find('moon', 'D9'), manifestation };
};

/**
 * Three levels of equal weight:
 * core nature (Moon D1) · inner potential (Moon D9) · manifestation (Ascendant + Sun D1).
 * @param {Object} profile - result of calculateVarnaProfile()
 * @param {'en'|'ru'} locale
 * @returns {{
 *   primary: Object|null,
 *   inner: Object|null,
 *   manifestation: Array<Object>,
 *   paragraphs: Array<{key: string, title?: string, text: string}>,
 * }}
 */
export function getVarnaInterpretation(profile, locale = 'en') {
  const lang = resolveLocale(locale);
  const t = TEMPLATES[lang];
  const levels = profile ? resolveLevels(profile) : null;
  const c = VARNA_INTERPRETATION[levels?.core]?.[lang];
  if (!c) return { primary: null, inner: null, manifestation: [], paragraphs: [] };

  const innerId = VARNA_INTERPRETATION[levels.inner] ? levels.inner : null;
  const i = innerId ? VARNA_INTERPRETATION[innerId][lang] : null;
  const manifestation = (levels.manifestation || []).filter((m) => VARNA_INTERPRETATION[m.varna]);
  const textOf = (id) => VARNA_INTERPRETATION[id][lang];

  const levelIds = unique([levels.core, innerId, ...manifestation.map((m) => m.varna)]);

  const openingParts = [t.opening.core({ ...c, pull: bold(c.pull) })];
  if (i) {
    openingParts.push(
      innerId === levels.core
        ? t.opening.innerSame()
        : t.opening.inner({ ...i, pull: bold(i.pull) })
    );
  }
  if (manifestation.length) {
    openingParts.push(t.opening.manifestation(
      unique(manifestation.map((m) => textOf(m.varna).expression)).map(bold)
    ));
  }
  const paragraphs = [{
    key: 'summary',
    text: `${t.motto(levelIds.map((id) => bold(textOf(id).verb)))} ${capitalize(t.clauses(openingParts))}.`,
  }];

  paragraphs.push({ key: 'core', title: t.titles.core, varnas: [levels.core], text: t.core(c) });

  if (i) {
    paragraphs.push({
      key: 'inner',
      title: t.titles.inner,
      varnas: [innerId],
      text: innerId === levels.core ? t.innerSame(i) : t.inner(i),
    });
  }

  manifestation.forEach((m, index) => {
    const m10n = textOf(m.varna);
    const text = m.strong
      ? t.manifestationStrong(m10n)
      : m.sources[0] === 'sun'
        ? t.manifestationSun(m10n)
        : t.manifestationAscendant(m10n);
    paragraphs.push({
      key: `manifestation_${m.sources.join('_')}`,
      title: index === 0 ? t.titles.manifestation : undefined,
      varnas: [m.varna],
      text,
    });
  });

  const other = levelIds.find((id) => id !== levels.core);
  const fulfilmentTarget = manifestation.find((m) => m.varna !== levels.core)?.varna || other;
  const interaction = [t.roles(levelIds.map((id) => textOf(id).role))];
  if (fulfilmentTarget) {
    interaction.push(t.fulfilment(c, textOf(fulfilmentTarget)));
    interaction.push(t.tension(c, textOf(other)));
  } else {
    interaction.push(c.challenges);
  }
  paragraphs.push({
    key: 'interaction',
    title: t.titles.interaction,
    varnas: levelIds,
    text: interaction.join(' '),
  });

  return {
    primary: describe(levels.core, lang),
    inner: innerId ? describe(innerId, lang) : null,
    manifestation: manifestation.map((m) => ({ ...describe(m.varna, lang), sources: m.sources, strong: m.strong })),
    paragraphs,
  };
}
